import { useState } from "react";
import { Link } from "react-router-dom";
import { PackageSearch, BookOpen } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { SEO } from "../components/SEO";
import { FieldError } from "../components/site/FieldError";
import { validators } from "../lib/validation";
import { toast } from "sonner";

const BADGE = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-700",
  expired: "bg-zinc-100 text-zinc-600",
  refunded: "bg-blue-100 text-blue-800",
};

const HINTS = {
  pending: "Payment not completed yet. If you paid, it will activate automatically within minutes.",
  paid: "Active subscription. Enjoy reading!",
  failed: "Payment did not go through. You were not charged.",
  expired: "This unpaid order expired after 24 hours. Please place a new order.",
  refunded: "This order was refunded.",
};

export default function TrackOrderPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    const msg = validators.email(email);
    setError(msg);
    if (msg) return;
    setBusy(true);
    try {
      const res = await api.get("/orders/lookup", { email: email.trim().toLowerCase() });
      setOrders(res.orders);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-16 md:py-24" data-testid="track-order-page">
      <SEO title="Track Your Order" path="/track" description="Look up your BookMala subscription orders with the email you used at checkout." />
      <span className="eyebrow">Order Lookup</span>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 text-bm-ink">Track your order</h1>
      <p className="text-bm-body mt-4 leading-relaxed">
        Enter the email you used at checkout to see your orders and active subscriptions - no account needed.
      </p>
      <form onSubmit={lookup} className="mt-8" data-testid="track-order-form" noValidate>
        <div className="flex flex-col sm:flex-row gap-3">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            onBlur={() => email && setError(validators.email(email))}
            placeholder="you@example.com" data-testid="track-email-input" aria-invalid={!!error}
            className={`bm-input flex-1 ${error ? "!border-red-400" : ""}`} />
          <button type="submit" disabled={busy} data-testid="track-lookup-btn" className="btn-primary shrink-0">
            <PackageSearch className="w-4 h-4" /> {busy ? "Looking up..." : "Find My Orders"}
          </button>
        </div>
        <FieldError msg={error} id="track-email" />
      </form>

      {orders && (
        <div className="mt-10 space-y-4" data-testid="track-results">
          {orders.length === 0 && (
            <div className="bm-card p-8 text-center" data-testid="track-empty">
              <p className="font-display text-xl font-semibold text-bm-ink">No orders found</p>
              <p className="text-sm text-bm-muted mt-2">We couldn't find any orders for this email. Check the spelling, or</p>
              <Link to="/pricing" className="btn-primary mt-5" data-testid="track-browse-plans">Browse Plans</Link>
            </div>
          )}
          {orders.map((o) => (
            <div key={o.order_id} className="bm-card p-6" data-testid={`track-order-${o.order_number}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-mono text-xs text-bm-muted">{o.order_number} · {(o.created_at || "").slice(0, 10)}</div>
                  <h3 className="font-display font-semibold text-lg text-bm-ink mt-1">{o.magazine_title}</h3>
                  <p className="text-sm text-bm-muted">{o.plan_name} · ₹{(o.amount || 0).toLocaleString("en-IN")}</p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${BADGE[o.status] || "bg-zinc-100 text-zinc-600"}`} data-testid={`track-status-${o.order_number}`}>
                  {o.status}
                </span>
              </div>
              <p className="text-xs text-bm-muted mt-3">{HINTS[o.status]}</p>
              {o.status === "paid" && o.access_token && o.magazine_slug && (
                <Link to={`/read/${o.magazine_slug}?key=${o.access_token}`} data-testid={`track-read-${o.order_number}`}
                  className="btn-secondary mt-4 !py-2.5 !px-5 !text-sm">
                  <BookOpen className="w-4 h-4" /> Read Full Issue
                </Link>
              )}
              {(o.status === "failed" || o.status === "expired") && (
                <Link to="/pricing" data-testid={`track-reorder-${o.order_number}`} className="btn-primary mt-4 !py-2.5 !px-5 !text-sm">
                  Order Again
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
